import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// ==========================
// 1. Fetch all users
// ==========================
export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to fetch users';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ==========================
// 2. Save user (create/update)
// ==========================
export const saveUser = createAsyncThunk(
  'user/saveUser',
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      const { id, ...data } = userData;
      let response;

      if (id) {
        response = await axios.patch(`${API_URL}/users/${id}`, data);
      } else {
        response = await axios.post(`${API_URL}/users`, data);
      }

      // Refresh users list
      await dispatch(fetchUsers());
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save user';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

// ==========================
// 3. Delete user
// ==========================
export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/users/${id}`);
      await dispatch(fetchUsers());
      return id;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete user';
      return rejectWithValue(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    list: [],
    status: 'idle', 
    error: null,
  },
  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload.map((user) => ({
          ...user,
          employeeId: user.employeeId ?? null,
          companyId: user.companyId ?? null,
        }));
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch users';
      })

      // Save User
      .addCase(saveUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(saveUser.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(saveUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to save user';
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = state.list.filter((user) => user.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete user';
      });
  },
});

export const { clearUserError } = userSlice.actions;
export default userSlice.reducer;
